import { defineStore } from 'pinia'
import { api, TOKEN_KEY, type LoginResult, type SecondStep } from '@/api/client'
import type { CaptchaAnswer } from '@/utils/captcha'

// Only admin/operator accounts can ever authenticate against the admin
// engine (see service.Auth's allowedRoles in control/internal/service) —
// there is no "customer" case to guard against here.
export interface AdminUser {
  id: number
  username: string
  email: string
  role: 'admin' | 'operator'
  status: string
  phone?: string
  created_at?: string
  /** 安全中心: when the MFA device was bound; absent while none is. */
  mfa_enabled_at?: string
  /** Absent for a password set before 安全中心 recorded changes. */
  password_changed_at?: string
  /** The phone was proved with a texted code. */
  phone_verified_at?: string
  /** 登录保护 by text message. */
  login_sms?: boolean
  /** 账户资料: the nickname and the avatar's address, when there are. */
  nickname?: string
  avatar_url?: string
}

interface State {
  token: string | null
  user: AdminUser | null
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    token: localStorage.getItem(TOKEN_KEY),
    user: null,
  }),
  getters: {
    isLoggedIn: (s) => !!s.token,
  },
  actions: {
    // Signs in, or — for an account with a second step (an MFA device,
    // 登录保护 by text) — returns what loginMFA needs: the ticket, the ways
    // to finish, the masked number a text goes to.
    async login(username: string, password: string, captcha?: CaptchaAnswer): Promise<LoginResult | null> {
      const res = await api.login(username, password, captcha)
      if (res.mfa_required) return res
      this.signIn(res)
      return null
    },
    async loginMFA(ticket: string, code: string, method: SecondStep = 'mfa') {
      this.signIn(await api.loginMFA(ticket, code, method))
    },
    signIn(res: LoginResult) {
      this.user = res.user
      this.useToken(res.access_token ?? '')
    },
    // A new token for this session: 修改密码 and 退出其他设备 end the old one.
    useToken(token: string) {
      this.token = token
      localStorage.setItem(TOKEN_KEY, token)
    },
    async refreshMe() {
      if (!this.token) return
      try {
        this.user = await api.me()
      } catch (e) {
        // Signed out only when the server says the session is over; a
        // network failure or a server error keeps it, for a reload to try
        // again — a slow link must not cost the sign-in.
        const status = (e as { response?: { status?: number } }).response?.status
        if (status === 401 || status === 403) this.logout()
      }
    },
    logout() {
      this.token = null
      this.user = null
      localStorage.removeItem(TOKEN_KEY)
      // Re-direct happens in the router guard.
    },
  },
})
